'use client';

import { useEffect, useRef } from 'react';

const benefits = [
  {
    icon: '⚡',
    title: 'Instant Pairing',
    desc: 'No proposals. No interviews. No waiting rooms. Get matched with the right expert in under 60 seconds.',
    color: '#FF3008',
    bg: '#FFF5F5',
    span: 'col-span-1',
  },
  {
    icon: '🖥️',
    title: 'Live Collaboration',
    desc: 'Every session includes real-time chat, file sharing, and built-in screen sharing. Your Genie works with you, not for you.',
    color: '#6C5CE7',
    bg: '#F0EDFF',
    span: 'col-span-1 md:col-span-2',
  },
  {
    icon: '🔒',
    title: 'Escrow Protection',
    desc: 'Your payment is held securely until you approve the work. Never pay for results you didn\'t get.',
    color: '#FFB800',
    bg: '#FFFBEB',
    span: 'col-span-1 md:col-span-2',
  },
  {
    icon: '💸',
    title: 'Fast Payouts',
    desc: 'Genies get paid the moment work is approved. Instant, no 30-day delays.',
    color: '#FF3008',
    bg: '#FFF5F5',
    span: 'col-span-1',
  },
  {
    icon: '🏢',
    title: 'Agency-Ready',
    desc: 'White-label sessions, team accounts, and bulk booking. Built for agencies managing multiple clients.',
    color: '#6C5CE7',
    bg: '#F0EDFF',
    span: 'col-span-1',
  },
  {
    icon: '🛡️',
    title: 'Dispute Protection',
    desc: 'Our mediation team has your back if anything goes wrong. Every session is documented for accountability.',
    color: '#FFB800',
    bg: '#FFFBEB',
    span: 'col-span-1',
  },
];

export default function BenefitsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef?.current?.querySelectorAll('.reveal, .reveal-scale');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 lg:py-28 px-4 sm:px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 reveal">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted mb-3">The Summons Difference</p>
            <h2 className="text-4xl sm:text-5xl font-black text-foreground tracking-tight leading-tight">
              Why Founders Choose<br />
              <span className="text-primary">Summons Over Upwork.</span>
            </h2>
          </div>
          <p className="text-muted text-base max-w-sm leading-relaxed">
            We didn't build another gig marketplace. We built the fastest path from "I need help" to "work is done."
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {benefits?.map((benefit, i) => (
            <div
              key={i}
              className={`reveal-scale benefit-card bg-white rounded-3xl p-7 border border-border shadow-card relative overflow-hidden ${benefit?.span}`}
              style={{ transitionDelay: `${i * 0.07}s` }}
            >
              {/* Background glow */}
              <div
                className="absolute top-0 right-0 w-28 h-28 rounded-full blur-2xl opacity-15"
                style={{ background: benefit?.color }}
              />

              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl mb-5 relative"
                style={{ background: benefit?.bg }}
              >
                {benefit?.icon}
              </div>

              <h3 className="text-lg font-bold text-foreground mb-2">{benefit?.title}</h3>
              <p className="text-sm text-muted leading-relaxed">{benefit?.desc}</p>

              <div
                className="absolute bottom-0 left-0 h-1 w-full opacity-30 rounded-b-3xl"
                style={{ background: `linear-gradient(to right, ${benefit?.color}, transparent)` }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}